import { toast } from 'react-hot-toast';

const reportedErrors = new WeakSet<object>();
const reportedApiErrors = new WeakSet<object>();

const isReportedApiError = (reason: unknown) =>
  typeof reason === 'object' && reason !== null && reportedApiErrors.has(reason);

export const isAbortError = (reason: unknown) =>
  typeof reason === 'object' && reason !== null && 'name' in reason && reason.name === 'AbortError';

const getErrorMessage = (reason: unknown) => {
  const message =
    typeof reason === 'string'
      ? reason
      : typeof reason === 'object' && reason !== null && 'message' in reason
        ? String(reason.message ?? '')
        : '';
  const name =
    typeof reason === 'object' && reason !== null && 'name' in reason ? String(reason.name) : '';
  if (name === 'NotAllowedError' || message.includes('NotAllowedError'))
    return 'Разрешите браузеру доступ к камере.';
  if (name === 'NotFoundError') return 'Камера не найдена на устройстве.';
  if (/^(Failed to fetch|NetworkError|Load failed)/i.test(message))
    return 'Не удалось связаться с сервером. Проверьте подключение и попробуйте снова.';
  return message.trim() || 'Не удалось выполнить действие. Попробуйте ещё раз.';
};

const showError = (reason: unknown, message?: string) => {
  if (isAbortError(reason)) return;
  if (typeof reason === 'object' && reason !== null) {
    if (reportedErrors.has(reason)) return;
    reportedErrors.add(reason);
    // Deduplicate repeated reports from local and global handlers.
    // A later attempt must still be able to notify about the same Error object.
    setTimeout(() => reportedErrors.delete(reason), 0);
  }
  const text = message ?? getErrorMessage(reason);
  toast.error(text, { id: `error:${text}`, duration: 6000 });
};

export const reportError = (reason: unknown, message?: string) => {
  if (!isReportedApiError(reason)) showError(reason, message);
};

export const reportApiError = (reason: unknown) => {
  if (isAbortError(reason)) return;
  if (typeof reason === 'object' && reason !== null) reportedApiErrors.add(reason);
  showError(reason);
};

type ActionOptions = {
  signal?: AbortSignal;
  onError?: (reason: unknown) => void;
};

export const runAction = async <T>(
  action: () => Promise<T>,
  { signal, onError }: ActionOptions = {},
): Promise<T | undefined> => {
  try {
    return await action();
  } catch (reason) {
    if (signal?.aborted || isAbortError(reason)) return;
    if (!isReportedApiError(reason)) reportError(reason);
    onError?.(reason);
  }
};
